"use client";

import type { OneSatServices } from "@1sat/client";
import type { WalletInterface } from "@bsv/sdk";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { env } from "@/env";
import { useBapProfile, useInvalidateBapProfile } from "@/hooks/use-bap-profile";
import { type BAPProfile, getAccountIdentity, getAvailableIdentities, type ResolvedIdentity } from "@/lib/bap/core";
import { getAccountWif } from "@/lib/bap/utils";
import { trackCurrentDevice } from "@/lib/device-tracking-middleware";
import { STORAGE_KEYS } from "@/lib/storage-keys";
interface WalletState {
  wallet: WalletInterface;
  services: OneSatServices;
  destroy: () => Promise<void>;
}
interface IdentityContextValue {
  /** All available identities from decrypted backup */
  identities: ResolvedIdentity[];

  /** Currently selected identity */
  currentIdentity: ResolvedIdentity | null;

  /** Profile data for current identity (if published on-chain) */
  currentProfile: BAPProfile | null;

  /** Whether profile is being fetched */
  isLoadingProfile: boolean;

  /** Wallet-remote instance for the current identity (null until ready) */
  wallet: WalletInterface | null;

  /** 1Sat services from wallet-remote (null until wallet ready) */
  services: OneSatServices | null;

  /** Whether the wallet is being initialized */
  isWalletReady: boolean;

  /** Switch to a different identity */
  setCurrentIdentity: (bapId: string) => void;

  /** Refresh profile data from blockchain */
  refreshProfile: () => Promise<void>;

  /** Check if user has a backup loaded */
  hasBackup: boolean;
}
const IdentityContext = createContext<IdentityContextValue | undefined>(undefined);
function getBackupReconciliationKey(): string | null {
  const backupStr = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
  if (!backupStr) {
    return null;
  }
  try {
    const backup = JSON.parse(backupStr) as Record<string, unknown>;
    if (typeof backup.rootPk === "string") {
      return backup.rootPk;
    }
    if (typeof backup.xprv === "string") {
      return backup.xprv;
    }
    if (typeof backup.wif === "string") {
      return backup.wif;
    }
    return backupStr;
  } catch {
    return backupStr;
  }
}
export function IdentityProvider({
  children
}: {
  children: ReactNode;
}) {
  const [identities, setIdentities] = useState<ResolvedIdentity[]>([]);
  const [currentIdentity, setCurrentIdentityState] = useState<ResolvedIdentity | null>(null);
  const [walletState, setWalletState] = useState<WalletState | null>(null);
  const [isWalletReady, setIsWalletReady] = useState(false);
  const walletBapIdRef = useRef<string | null>(null);
  const reconciliationKeyRef = useRef<string | null>(null);
  const reconciliationRunningRef = useRef(false);
  const invalidateProfile = useInvalidateBapProfile();

  // Latest currentIdentity, readable from stable event handlers without
  // adding it to their effect's dependency array (which would re-subscribe
  // the listeners on every identity change).
  const currentIdentityRef = useRef(currentIdentity);
  useEffect(() => {
    currentIdentityRef.current = currentIdentity;
  }, [currentIdentity]);

  // Apply a freshly-derived identity list to state WITHOUT forcing a render
  // when the set of bapIds hasn't changed. getAvailableIdentities() returns a
  // brand-new array of new objects on every call, so a naive setIdentities
  // would change the reference each time and re-render consumers in a loop.
  // Keeping the previous reference when the bapId set is identical breaks that
  // loop (the wallet-init + profile-fetch effects key off this).
  const applyIdentities = useCallback((next: ResolvedIdentity[]) => {
    setIdentities(prev => {
      if (prev.length === next.length && prev.every((id, i) => id.bapId === next[i].bapId)) {
        return prev;
      }
      return next;
    });
  }, []);
  const scheduleIdentityReconciliation = useCallback(() => {
    if (reconciliationRunningRef.current) {
      return;
    }
    const reconciliationKey = getBackupReconciliationKey();
    if (!reconciliationKey || reconciliationKeyRef.current === reconciliationKey) {
      return;
    }
    reconciliationKeyRef.current = reconciliationKey;
    reconciliationRunningRef.current = true;
    import("@/lib/auth-flows").then(({
      reconcileUnlockedBackupIdentities
    }) => reconcileUnlockedBackupIdentities()).catch(error => {
      console.warn("Identity reconciliation failed:", error);
    }).finally(() => {
      reconciliationRunningRef.current = false;
    });
  }, []);

  // Fetch current profile via TanStack Query (replaces both profile effects)
  const currentProfileQuery = useBapProfile(currentIdentity?.bapId ?? null);

  // Load available identities on mount and when the backup actually changes.
  // Keyed only on stable callbacks so the listeners subscribe once — it must
  // NOT depend on currentIdentity (that would re-run loadIdentities every
  // selection change and re-enter the render loop).
  useEffect(() => {
    const loadIdentities = () => {
      const available = getAvailableIdentities();

      // Set identities immediately - don't block on profile fetching!
      applyIdentities(available);
      scheduleIdentityReconciliation();

      // Track device login (non-blocking, for security monitoring)
      if (available.length > 0) {
        trackCurrentDevice().catch(error_0 => {
          console.error("Device tracking failed (non-critical):", error_0);
        });
      }
    };
    loadIdentities();

    // Re-check when sessionStorage changes (backup loaded/cleared)
    const handleStorageChange = () => {
      loadIdentities();
    };

    // Re-check when draft profiles are updated or cleared
    const handleDraftUpdate = () => {
      loadIdentities();
      // Invalidate TanStack Query cache for current identity (read via ref
      // to avoid re-subscribing this listener on every identity change).
      const active = currentIdentityRef.current;
      if (active) {
        invalidateProfile(active.bapId);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("bap-draft-updated", handleDraftUpdate);
    window.addEventListener("bap-backup-updated", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("bap-draft-updated", handleDraftUpdate);
      window.removeEventListener("bap-backup-updated", handleStorageChange);
    };
  }, [applyIdentities, invalidateProfile, scheduleIdentityReconciliation]);

  // Pick the initial identity once a list is available and nothing is selected.
  // Derives from the `identities` STATE (content-stable) rather than calling
  // getAvailableIdentities() again — calling it here returned a fresh array
  // each render and, together with the setCurrentIdentityState below, formed a
  // read/write loop. Once currentIdentity is set the guard makes this a no-op.
  useEffect(() => {
    if (identities.length === 0 || currentIdentity) {
      return;
    }

    // Check if there's a saved selection
    const savedBapId = localStorage.getItem(STORAGE_KEYS.CURRENT_IDENTITY);
    const savedIdentity = identities.find(id_0 => id_0.bapId === savedBapId);

    // Use saved identity if valid, otherwise use first identity
    const initialIdentity = savedIdentity || identities[0];
    setCurrentIdentityState(initialIdentity);
    localStorage.setItem(STORAGE_KEYS.CURRENT_IDENTITY, initialIdentity.bapId);
  }, [identities, currentIdentity]);

  // Create wallet-remote when current identity changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on bapId only — avoid re-creating wallet when identity object ref changes
  useEffect(() => {
    if (!currentIdentity) {
      setWalletState(null);
      setIsWalletReady(false);
      walletBapIdRef.current = null;
      return;
    }

    // Skip if already initialized for this identity
    if (walletBapIdRef.current === currentIdentity.bapId) {
      return;
    }
    let cancelled = false;
    const initWallet = async () => {
      // Destroy previous wallet if any
      if (walletState) {
        await walletState.destroy();
        setWalletState(null);
      }
      setIsWalletReady(false);
      const master = getAccountIdentity(currentIdentity.bapId);
      if (!master) {
        return;
      }
      const wif = getAccountWif(master);
      const {
        createWebWallet
      } = await import("@1sat/wallet-browser");
      const walletUrl = env.NEXT_PUBLIC_WALLET_REMOTE_URL;
      const {
        wallet,
        services,
        destroy
      } = await createWebWallet({
        privateKey: wif,
        chain: "main",
        activeRemote: walletUrl,
        storageIdentityKey: currentIdentity.bapId
      });
      if (cancelled) {
        await destroy();
        return;
      }
      walletBapIdRef.current = currentIdentity.bapId;
      setWalletState({
        wallet: wallet as unknown as WalletInterface,
        services,
        destroy
      });
      setIsWalletReady(true);
    };
    initWallet().catch(error_1 => {
      console.error("Failed to initialize wallet-remote:", error_1);
    });
    return () => {
      cancelled = true;
    };
  }, [currentIdentity?.bapId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup wallet on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount-only cleanup — must not re-run when walletState changes
  useEffect(() => {
    return () => {
      walletState?.destroy();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setCurrentIdentity = (bapId: string) => {
    const identity = identities.find(id_1 => id_1.bapId === bapId);
    if (identity) {
      setCurrentIdentityState(identity);
      localStorage.setItem(STORAGE_KEYS.CURRENT_IDENTITY, bapId);
    }
  };
  const refreshProfile = (): Promise<void> => {
    if (currentIdentity) {
      invalidateProfile(currentIdentity.bapId);
    }
    return Promise.resolve();
  };
  const hasBackup = identities.length > 0;
  return <IdentityContext.Provider value={{
    identities,
    currentIdentity,
    currentProfile: currentProfileQuery.data ?? null,
    isLoadingProfile: currentProfileQuery.isLoading,
    wallet: walletState?.wallet ?? null,
    services: walletState?.services ?? null,
    isWalletReady,
    setCurrentIdentity,
    refreshProfile,
    hasBackup
  }}>
			{children}
		</IdentityContext.Provider>;
}
export function useIdentity() {
  const context = useContext(IdentityContext);
  if (context === undefined) {
    throw new Error("useIdentity must be used within an IdentityProvider");
  }
  return context;
}